import { kmeans } from 'ml-kmeans';
import { useCallback, useEffect, useState } from 'react';

import { OcrItem, useOcrTree } from '@/hooks';
import { Box, Point, rotatePoint } from '@/utils';

const MAX_KMEANS_ITERATIONS = 250;

type Cluster = {
  y: number;
  ocrItems: OcrItem[];
};

export type UseFilterOcrItemsOpts = {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  canvasVersion?: number;
  page?: number;
  rotation: number;
  selectionBox?: Box | null;
  scaleAnchorByDpr?: boolean;
};

export const useFilterOcrItems = ({
  canvasRef,
  canvasVersion = 0,
  page,
  rotation,
  selectionBox,
  scaleAnchorByDpr,
}: UseFilterOcrItemsOpts) => {
  const { search } = useOcrTree({ page });
  const [items, setItems] = useState<OcrItem[]>([]);
  const [anchor, setAnchor] = useState<Point | undefined>();
  const [text, setText] = useState<string | undefined>();

  const clear = useCallback(() => {
    setItems?.([]);
    setAnchor(undefined);
    setText(undefined);
  }, []);

  const getText = useCallback((ocrItems: OcrItem[]) => {
    if (!ocrItems.length) return;

    const withinY = (cluster: Cluster) => {
      for (const ocrItem of cluster.ocrItems) {
        const minY = Math.min(...ocrItem.polygon.map((p) => p[1]));
        const maxY = Math.max(...ocrItem.polygon.map((p) => p[1]));
        if (minY > cluster.y || maxY < cluster.y) return false;
      }
      return true;
    };

    let clusters: Cluster[] = [];

    const checkClusters = () => {
      if (!clusters.length) return false;

      for (const cluster of clusters) {
        if (!withinY(cluster)) return false;
      }

      return true;
    };

    const kMeans = (k: number, items: OcrItem[]) => {
      const ys = items.map((item) => [item.polygon.reduce((sum, p) => sum + p[1], 0) / item.polygon.length]);
      const minY = Math.min(...ys.map((v) => v[0]));
      const maxY = Math.max(...ys.map((v) => v[0]));

      let centers;
      if (k == 1) {
        centers = [[(minY + maxY) / 2]];
      } else {
        centers = Array.from({ length: k }, (_, i) => [minY + (i * (maxY - minY)) / (k - 1)]);
      }

      const result = kmeans(ys, k, { initialization: centers });

      const newClusters = [];
      for (const [i, centroid] of result.centroids.entries()) {
        newClusters.push({
          y: centroid[0],
          ocrItems: ocrItems
            .filter((_, index) => result.clusters[index] === i)
            .sort((a, b) => {
              const aMinX = Math.min(...a.polygon.map((p) => p[0]));
              const bMinX = Math.min(...b.polygon.map((p) => p[0]));
              return aMinX - bMinX;
            }),
        });
      }
      return newClusters;
    };

    while (!checkClusters() && clusters.length + 1 <= Math.min(ocrItems.length, MAX_KMEANS_ITERATIONS)) {
      clusters = kMeans(clusters.length + 1, ocrItems);
    }

    return clusters.map((cluster) => cluster.ocrItems.map((o) => o.text).join(' ')).join('\n') ?? undefined;
  }, []);

  const getAnchor = useCallback(
    (ocrItems: OcrItem[]) => {
      if (ocrItems.length === 0) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const { width, height } = canvas;

      const xValues = ocrItems.flatMap((item) => item.polygon).map((p) => p[0]);
      const yValues = ocrItems.flatMap((item) => item.polygon).map((p) => p[1]);

      let anchor: Point;

      switch (rotation) {
        case 0:
          anchor = { x: Math.min(...xValues), y: Math.max(...yValues) };
          break;
        case 90:
          anchor = { x: Math.max(...xValues), y: Math.max(...yValues) };
          break;
        case 180:
          anchor = { x: Math.max(...xValues), y: Math.min(...yValues) };
          break;
        case 270:
          anchor = { x: Math.min(...xValues), y: Math.min(...yValues) };
          break;
        default:
          anchor = { x: Math.min(...xValues), y: Math.max(...yValues) };
      }

      anchor = rotatePoint(anchor, rotation);

      anchor.x = anchor.x * width;
      anchor.y = anchor.y * height + 20;

      if (scaleAnchorByDpr) {
        const dpr = window.devicePixelRatio || 1;
        anchor.x /= dpr;
        anchor.y /= dpr;
      }

      return anchor;
    },
    [canvasRef, rotation, scaleAnchorByDpr]
  );

  useEffect(() => {
    if (!selectionBox || !canvasVersion) {
      clear();
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      clear();
      return;
    }

    const p0 = rotatePoint(
      {
        x: selectionBox.x,
        y: selectionBox.y,
      },
      -rotation
    );
    const p1 = rotatePoint(
      {
        x: selectionBox.x + selectionBox.w,
        y: selectionBox.y + selectionBox.h,
      },
      -rotation
    );

    const bbox = {
      minX: Math.min(p0.x, p1.x),
      minY: Math.min(p0.y, p1.y),
      maxX: Math.max(p0.x, p1.x),
      maxY: Math.max(p0.y, p1.y),
    };

    const bboxArea = (bbox.maxX - bbox.minX) * (bbox.maxY - bbox.minY);

    let boxes = [];
    if (bboxArea < 0.0001) {
      boxes = search(bbox, 'line');
    } else {
      boxes = search(bbox, 'word');
    }

    setItems(boxes);
    setText(getText(boxes));
    setAnchor(getAnchor(boxes));
  }, [selectionBox, rotation, canvasRef, canvasVersion, search, clear, getText, getAnchor]);

  return { items, anchor, text };
};
